import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  DocumentTextIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { quotesService } from '../lib/db/quotes';
import { customersService } from '../lib/db/customers';
import { projectsService } from '../lib/db/projects';
import { contractsService } from '../lib/db/contracts';
import { contactsService } from '../lib/db/contacts';
import { db } from '../lib/cloudsql';
import { formatUnitPriceDisplay, formatEmailAmount } from '../utils/formatNumberInput';
import {
  calculateLineAmounts,
  isMarkupRate,
  isMarkupRateUnit,
  isProcurementServiceType,
  resolveQuoteItemRates,
  summarizeQuoteItemTotals
} from '../utils/quoteItemAmounts';
import { QuoteItemsEditor } from '../components/QuoteItemsEditor';
import { downloadQuotePdf, generateQuotePdfBlob, QuoteData as PdfQuoteData } from '../lib/htmlQuotePdf';
import { sendQuoteEmail, getEmailTemplates, replaceTemplateVariables } from '../lib/gmailApi';
import { getCurrentUser } from '../lib/auth';
import { isContactActive } from '../utils/contactEmail';
import { useQuoteItems, type QuoteItem, type ServiceRate, type ContractServiceItem, normalizeServiceTypeKey } from '../hooks/useQuoteItems';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

interface Quote {
  id?: string;
  customer_id: string;
  project_id?: string;
  contract_id?: string;
  quote_number: string;
  quote_name: string;  // 修正為 quote_name
  status: 'draft' | 'sent' | 'signed_back' | 'failed' | 'expired';
  issue_date: string;
  valid_until: string;
  subtotal?: number;
  subtotal_amount?: number; // 小計金額（自動計算：sum of quote_items.subtotal）
  tax_rate: number;
  tax_amount?: number;
  total_amount?: number;
  discounted_total: number;
  subtotal_afterdiscount?: number; // 折扣後小計金額（自動計算：sum of quote_items.subtotal_afterdiscount）
  terms?: string;
  contact_ids?: string[];
  primary_contact_id?: string;
  signed_back_file_url?: string;
  signed_back_date?: string;
  creator_name?: string;
  creator_email?: string;
  customer_name: string;
  customer_tax_id?: string;
  total_discount: number;
  subtotal_discount?: number; // 折扣金額（自動計算：sum of quote_items.subtotal_discount）
  created_at?: string;
  updated_at?: string;
}

interface Contract {
  id: string;
  contract_number: string;
  title: string;
  customer_id: string;
  status: string;
  start_date?: string;
  end_date?: string;
}

interface Template {
  id: string;
  name: string;
  type: string;
  content?: string;
}

interface DefaultTerm {
  id: string;
  name: string;
  content: string;
  is_active: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

interface Customer {
  id: string;
  company: string;
  tax_id?: string;
  owner?: string;
}

interface Project {
  id: string;
  name: string;
  customer_id: string;
}

interface Contact {
  id: string;
  customer_id: string;
  name: string;
  email: string;
  phone: string;
  position: string;
  status?: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  customers?: {
    company: string;
  };
}

const Quotes: React.FC = () => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [serviceRates, setServiceRates] = useState<ServiceRate[]>([]);
  const [contractServiceItems, setContractServiceItems] = useState<ContractServiceItem[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [defaultTerms, setDefaultTerms] = useState<DefaultTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentQuote, setCurrentQuote] = useState<Quote | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState<string>('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImportData, setBulkImportData] = useState('');
  const [bulkImportMode, setBulkImportMode] = useState<'full' | 'category'>('full');
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusQuote, setStatusQuote] = useState<Quote | null>(null);
  const [newStatus, setNewStatus] = useState<string>('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailQuote, setEmailQuote] = useState<Quote | null>(null);
  const [emailData, setEmailData] = useState({
    recipientEmail: '',
    recipientName: '',
    subject: '',
    body: ''
  });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [signedBackFileUrl, setSignedBackFileUrl] = useState<string>('');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [primaryContactId, setPrimaryContactId] = useState<string>('');
  
  // 郵件模板相關狀態
  const [emailTemplates, setEmailTemplates] = useState<Array<{
    id: string;
    name: string;
    type: string;
    subject: string;
    body: string;
    variables?: string[];
  }>>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<{
    id: string;
    name: string;
    type: string;
    subject: string;
    body: string;
    variables?: string[];
  } | null>(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string>('');

  // 取得當年度服務收費表（單價與單位）
  const getCurrentYearRate = useCallback((serviceType: string, fallbackPrice?: number): { price: number; unit?: string } => {
    if (!serviceType || !serviceRates || serviceRates.length === 0) {
      return { price: fallbackPrice ?? 0 };
    }

    const currentYear = new Date().getFullYear();
    
    // 優先查找當年度（2026）服務收費，嚴格以 year 欄位為準
    const currentYearRate = serviceRates.find(
      rate => rate.service_type === serviceType && // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (rate as any).year === currentYear
    );
    
    if (currentYearRate) {
      // 優先使用 unit_price，如果沒有則使用 hourly_rate
      const price = // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (currentYearRate as any).unit_price || // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (currentYearRate as any).hourly_rate || 0;
      if (price > 0) {
        return { price, unit: currentYearRate.unit };
      }
    }

    // 如果找不到當年度，查找最新年度的服務收費
    const matchingRates = serviceRates.filter(rate => rate.service_type === serviceType);
    if (matchingRates.length > 0) {
      // 按年度降序排序，取最新的
      const sortedRates = matchingRates.sort((a, b) => {
        const yearA = // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (a as any).year ?? 0;
        const yearB = // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (b as any).year ?? 0;
        return yearB - yearA;
      });
      // 優先使用 unit_price，如果沒有則使用 hourly_rate
      const price = // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (sortedRates[0] as any).unit_price || // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (sortedRates[0] as any).hourly_rate || 0;
      if (price > 0) {
        return { price, unit: sortedRates[0].unit };
      }
    }

    // 如果都找不到，使用備用價格（原本儲存的 list_price）
    return { price: fallbackPrice ?? 0 };
  }, [serviceRates]);

  const getCurrentYearListPrice = useCallback((serviceType: string, fallbackPrice?: number): number => {
    return getCurrentYearRate(serviceType, fallbackPrice).price;
  }, [getCurrentYearRate]);

  /**
   * 建立 PDF 用的報價項目資料。
   * 工時：牌價 = 當年度服務收費表；合約價 = 合約議定價格。
   * 採購：不走收費表、無牌價；合約價＝使用者填的收費比例。
   */
  const buildQuoteItemsForPdf = useCallback(async (
    itemsData: Array<{ item_name?: string; service_type?: string; unit_price?: number; list_price?: number; contract_price?: number; quantity?: number; unit?: string; is_deduction?: boolean }>,
    quote: Quote | null
  ) => {
    if (!itemsData || itemsData.length === 0) return [];
    let contractItems: ContractServiceItem[] = [];
    if (quote?.contract_id) {
      try {
        const raw = await contractsService.getServiceItems(quote.contract_id) as ContractServiceItem[];
        contractItems = raw.map((row) => ({
          ...row,
          contract_id: row.contract_id || quote.contract_id!
        }));
      } catch {
        // 忽略合約載入失敗
      }
    }
    return itemsData
      .map(item => {
        const isDeduction = item.is_deduction || false;
        const isProcurement = isProcurementServiceType(item.service_type) || isMarkupRateUnit(item.unit);
        if (isProcurement) {
          const contractPrice = Number(item.contract_price) || 0;
          const quantity = Number(item.quantity) || 0;
          const amounts = calculateLineAmounts({
            listPrice: contractPrice,
            contractPrice,
            quantity,
            unit: '%',
            forceMarkup: true,
            isDeduction
          });
          return {
            item_name: item.item_name || '',
            service_type: item.service_type || '',
            list_price: contractPrice,
            contract_price: contractPrice,
            unit: '%',
            quantity,
            estimated_hours: quantity,
            subtotal: amounts.subtotal,
            discount_amount: 0,
            discounted_total: amounts.subtotal_afterdiscount
          };
        }
        const catalog = getCurrentYearRate(item.service_type || '', item.list_price);
        const listStored = Number(catalog.price || item.unit_price || item.list_price) || 0;
        const want = normalizeServiceTypeKey(item.service_type);
        const contractItem = contractItems.find(
          (c: ContractServiceItem) => normalizeServiceTypeKey(c.service_type) === want
        );
        const resolved = resolveQuoteItemRates({
          listPrice: listStored,
          listUnit: catalog.unit || item.unit,
          contractPrice: contractItem?.contract_price ?? item.contract_price ?? listStored,
          contractUnit: contractItem?.unit || item.unit,
          hasContractMatch: Boolean(contractItem) || item.contract_price != null
        });
        const quantity = Number(item.quantity) || 0;
        const amounts = calculateLineAmounts({
          listPrice: resolved.listPrice,
          contractPrice: resolved.contractPrice,
          quantity,
          unit: resolved.unit,
          isDeduction
        });
        return {
          item_name: item.item_name || '',
          service_type: item.service_type || '',
          list_price: resolved.listPrice,
          contract_price: resolved.contractPrice,
          unit: resolved.unit,
          quantity,
          estimated_hours: quantity,
          subtotal: amounts.subtotal,
          discount_amount: amounts.subtotal_discount,
          discounted_total: amounts.subtotal_afterdiscount
        };
      });
  }, [getCurrentYearRate]);

  // 計算總計金額
  const calculateTotals = useCallback((items: QuoteItem[]) => {
    const totals = summarizeQuoteItemTotals(items);

    // 只在 currentQuote 存在時更新，並檢查值是否真的改變，避免觸發無限循環
    setCurrentQuote(prev => {
      if (!prev) return null;
      if (prev.subtotal_amount === totals.subtotal &&
          prev.subtotal_discount === totals.discount &&
          prev.subtotal_afterdiscount === totals.afterDiscount &&
          prev.tax_amount === totals.tax &&
          prev.total_amount === totals.total) {
        return prev;
      }
      return {
        ...prev,
        subtotal: totals.subtotal,
        subtotal_amount: totals.subtotal,
        subtotal_discount: totals.discount,
        subtotal_afterdiscount: totals.afterDiscount,
        tax_rate: 5,
        tax_amount: totals.tax,
        total_amount: totals.total,
        total_discount: totals.discount,
        discounted_total: totals.afterDiscount
      };
    });
  }, []);

  const {
    quoteItems,
    addQuoteItem,
    addMarkupQuoteItem,
    updateQuoteItem,
    removeQuoteItem,
    setQuoteItemsBatch,
    clearQuoteItems,
    updateItemPrices
  } = useQuoteItems(calculateTotals);

  const fetchQuotes = useCallback(async () => {
    try {
      setLoading(true);
      
      // 先檢查並更新過期報價單狀態
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        const response = await fetch(`${apiUrl}/api/quotes/check-expired`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) {
          // 如果不是 2xx 狀態碼，記錄警告但不影響載入報價單列表
          console.warn(`檢查過期報價單失敗: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        // 檢查過期報價單失敗不影響載入報價單列表
        console.warn('檢查過期報價單失敗:', error);
      }
      
      // 載入報價單列表
      const data = await quotesService.getAll([{ field: 'created_at', direction: 'desc' }]);

      // 確保數值欄位正確轉換為數字（PostgreSQL NUMERIC 類型可能返回字符串）
      const processedData = (data || []).map((quote: unknown) => {
        const q = quote as Quote;
        return {
          ...q,
          total_amount: typeof q.total_amount === 'number' ? q.total_amount : (typeof q.total_amount === 'string' ? parseFloat(q.total_amount) || 0 : 0),
          discounted_total: typeof q.discounted_total === 'number' ? q.discounted_total : (typeof q.discounted_total === 'string' ? parseFloat(q.discounted_total) || 0 : 0),
          subtotal: typeof q.subtotal === 'number' ? q.subtotal : (typeof q.subtotal === 'string' ? parseFloat(q.subtotal) || 0 : 0),
          subtotal_amount: typeof q.subtotal_amount === 'number' ? q.subtotal_amount : (typeof q.subtotal_amount === 'string' ? parseFloat(q.subtotal_amount) || 0 : 0),
          tax_rate: typeof q.tax_rate === 'number' ? q.tax_rate : (typeof q.tax_rate === 'string' ? parseFloat(q.tax_rate) || 5 : 5),
          tax_amount: q.tax_amount ? (typeof q.tax_amount === 'number' ? q.tax_amount : (typeof q.tax_amount === 'string' ? parseFloat(q.tax_amount) || 0 : 0)) : 0,
          total_discount: typeof q.total_discount === 'number' ? q.total_discount : (typeof q.total_discount === 'string' ? parseFloat(q.total_discount) || 0 : 0)
        };
      });

      // 篩選資料
      let filteredData = processedData;
      if (selectedCustomerFilter) {
        filteredData = filteredData.filter(q => q.customer_id === selectedCustomerFilter);
      }
      if (selectedStatusFilter) {
        filteredData = filteredData.filter(q => q.status === selectedStatusFilter);
      }

      setQuotes(filteredData as Quote[]);
    } catch {
      alert('取得報價單列表失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, [selectedCustomerFilter, selectedStatusFilter]);

  useEffect(() => {
    fetchQuotes();
    fetchCustomers();
    fetchProjects();
    fetchContracts();
    fetchServiceRates();
    fetchTemplates();
    fetchDefaultTerms();
    fetchContacts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchQuotes]);

  // 當 quoteItems 變化時，重新計算總金額
  // 注意：calculateTotals 已經在 useQuoteItems 的 onItemsChange 回調中被調用
  // 這裡的 useEffect 只是作為備用，確保在特定情況下也能計算
  useEffect(() => {
    // 只在有 currentQuote 且有 quoteItems 時才計算
    // 使用 ref 來追蹤是否正在計算，避免重複調用
    if (currentQuote && quoteItems.length > 0) {
      // 使用 setTimeout 來避免在渲染期間同步調用 setState
      const timer = setTimeout(() => {
        calculateTotals(quoteItems);
      }, 0);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteItems]); // 只依賴 quoteItems，calculateTotals 是穩定的 useCallback，不需要在依賴中

  const fetchCustomers = async () => {
    try {
      const data = await customersService.getAll([{ field: 'company', direction: 'asc' }]);
      setCustomers(data.map(c => ({ id: c.id, company: c.company, tax_id: c.tax_id, owner: c.owner })));
    } catch {
      // 取得客戶列表失敗
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await projectsService.getAll([{ field: 'name', direction: 'asc' }]);
      setProjects(data.map(p => ({ id: p.id, name: p.name, customer_id: p.customer_id })));
    } catch {
      // 取得專案列表失敗
    }
  };

  const fetchContracts = async () => {
    try {
      const data = await contractsService.getAll([{ field: 'created_at', direction: 'desc' }]);
      setContracts(data.map(c => ({
        id: c.id,
        contract_number: c.contract_number || c.id, // 優先使用 contract_number，如果沒有則使用 id
        title: c.title,
        customer_id: c.customer_id,
        status: 'active',
        start_date: c.start_date,
        end_date: c.end_date
      })));
    } catch {
      // 取得合約列表失敗
    }
  };

  const fetchServiceRates = async () => {
    try {
      const data = await db.query<ServiceRate>('service_rates', {
        where: { is_active: true },
        orderBy: [{ field: 'service_type', direction: 'asc' }]
      });
      // 處理服務收費表資料，確保包含 unit_price 和 year
      // 年份僅從 description 解析，未標註年份的舊資料不預設為當年（以利區分 2026 與舊年度）
      const mappedData = (data || []).map(rate => {
        let unit = '小時';
        let year: number | undefined;
        if (rate.description) {
          const unitMatch = rate.description.match(/單位:\s*([^|]+)/);
          if (unitMatch) {
            unit = unitMatch[1].trim();
          }
          const yearMatch = rate.description.match(/年份:\s*(\d+)/);
          if (yearMatch) {
            year = parseInt(yearMatch[1]);
          }
        }
        // 優先使用 unit_price，如果沒有則使用 hourly_rate
        const unitPrice = // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (rate as any).unit_price || // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (rate as any).hourly_rate || 0;
        const chargeType = // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (rate as any).charge_type || 'fixed';
        const parsedUnit = // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (rate as any).unit || unit;
        return {
          ...rate,
          unit_price: unitPrice,
          unit: chargeType === 'percentage' ? '%' : parsedUnit,
          year: // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (rate as any).year ?? year,
          charge_type: chargeType
        };
      });
      setServiceRates(mappedData);
      
      // 服務收費表載入完成後，如果當前有報價項目，重新計算金額
      if (quoteItems.length > 0 && currentQuote) {
        // 更新所有項目的 unit_price（如果為 0 或未設置）
        const updatedItems = quoteItems.map(item => {
          if (item.service_type && (!item.unit_price || item.unit_price === 0)) {
            const listPrice = getCurrentYearListPrice(item.service_type, item.list_price);
            if (listPrice > 0) {
              return {
                ...item,
                unit_price: listPrice,
                list_price: item.list_price || listPrice
              };
            }
          }
          return item;
        });
        // 如果有項目被更新，重新設置並計算
        if (updatedItems.some((item, index) => item.unit_price !== quoteItems[index]?.unit_price)) {
          setQuoteItemsBatch(updatedItems);
        }
      }
    } catch {
      // 取得服務費率失敗
    }
  };

  const fetchContractServiceItems = async (contractId: string) => {
    try {
      // 先檢查合約是否存在
      const contract = await contractsService.getById(contractId);
      
      if (!contract) {
        // 查詢合約失敗，記錄錯誤但不顯示給用戶
        setContractServiceItems([]);
        return;
      }

      const data = await contractsService.getServiceItems(contractId);
      const withContractId: ContractServiceItem[] = (data || []).map((row) => ({
        ...row,
        contract_id: (row as ContractServiceItem).contract_id || contractId,
        unit: (row as ContractServiceItem).unit || '小時'
      }));
      
      if (withContractId.length > 0) {
        // 合約服務項目載入完成後，自動重新計算所有已選擇服務類型的價格
        if (quoteItems.length > 0) {
          quoteItems.forEach((item, index) => {
            if (item.service_type) {
              updateItemPrices(
                index,
                item.service_type,
                serviceRates,
                withContractId,
                contractId
              );
            }
          });
        }
      }
      
      setContractServiceItems(withContractId);
    } catch {
      setContractServiceItems([]);
    }
  };

  const fetchTemplates = async () => {
    try {
      // 暫時停用模板功能，因為我們現在使用 quote_terms 作為條款
      setTemplates([]);
    } catch {
      // Error fetching templates
    }
  };

  const fetchDefaultTerms = async () => {
    try {
      const data = await db.query<DefaultTerm>('quote_terms', {
        where: { is_default: true },
        orderBy: [
          { field: 'name', direction: 'asc' }
        ]
      });
      setDefaultTerms(data);
    } catch {
      // 取得預設條款失敗
    }
  };

  const fetchContacts = async () => {
    try {
      const [contactsData, customersData] = await Promise.all([
        contactsService.getAll(),
        customersService.getAll([{ field: 'company', direction: 'asc' }]),
      ]);
      const customerMap = new Map(customersData.map((c) => [c.id, c]));
      const contactsWithCustomers: Contact[] = contactsData.map((contact) => ({
        ...contact,
        email: contact.email || '',
        phone: contact.phone || '',
        position: contact.position || '',
        customers: customerMap.get(contact.customer_id)
          ? { company: customerMap.get(contact.customer_id)!.company }
          : undefined,
      }));
      setContacts(contactsWithCustomers);
    } catch {
      // 取得聯絡人列表失敗
    }
  };

  const createNewQuote = () => {
    const today = new Date().toISOString().slice(0, 10);
    const validUntil = new Date();
    validUntil.setDate(validUntil.getDate() + 30);
    let defaultTerms = '';
    if (templates.length > 0) {
      // 若有 type 為 default 的條款，優先用，否則用第一筆
      const defaultTemplate = templates.find(t => t.type === 'default') || templates[0];
      defaultTerms = defaultTemplate.content || '';
    }
    const user = getCurrentUser();
    const newQuote: Quote = {
      customer_id: customers.length > 0 ? customers[0].id : '',
      customer_name: customers.length > 0 ? customers[0].company : '',
      quote_number: generateQuoteNumber(),
      quote_name: '',
      status: 'draft',
      issue_date: today,
      valid_until: validUntil.toISOString().slice(0, 10),
      subtotal: 0,
      tax_rate: 5,
      tax_amount: 0,
      total_amount: 0,
      discounted_total: 0,
      total_discount: 0,
      terms: defaultTerms,
      contact_ids: [],
      creator_name: user?.displayName || user?.email || '',
      creator_email: user?.email || ''
    };
    setCurrentQuote(newQuote);
    clearQuoteItems();
    setContractServiceItems([]);
    setSelectedContacts([]);
    setPrimaryContactId('');
    setIsEditing(false);
    setShowCreateModal(true);
  };

  // 將 editQuote 改為 async，並載入 quote_items
  const editQuote = async (quote: Quote) => {
    // 確保日期格式正確（YYYY-MM-DD）
    const formatDateForInput = (dateStr: string | undefined): string => {
      if (!dateStr) return '';
      // 如果是 ISO 格式（包含時間），只取日期部分
      if (dateStr.includes('T')) {
        return dateStr.split('T')[0];
      }
      // 如果已經是 YYYY-MM-DD 格式，直接返回
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateStr;
      }
      // 嘗試解析其他格式
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
      return dateStr;
    };

    const formattedQuote = {
      ...quote,
      issue_date: formatDateForInput(quote.issue_date),
      valid_until: formatDateForInput(quote.valid_until)
    };

    setCurrentQuote(formattedQuote);
    setIsEditing(true);
    setShowCreateModal(true);

    // 載入報價單的項目資料
    try {
      const items = await quotesService.getQuoteItems(quote.id!);
      if (!items || items.length === 0) {
        // Error loading quote items
        clearQuoteItems();
      } else {
        // 載入合約服務項目（若有合約），用於重新計算 contract_price
        let contractItems: ContractServiceItem[] = [];
        if (quote.contract_id) {
          try {
            const raw = await contractsService.getServiceItems(quote.contract_id) as ContractServiceItem[];
            contractItems = raw.map((row) => ({
              ...row,
              contract_id: row.contract_id || quote.contract_id!
            }));
            setContractServiceItems(contractItems);
          } catch {
            setContractServiceItems([]);
          }
        } else {
          setContractServiceItems([]);
        }

        // 載入時以資料庫為準，但若有合約則依合約服務項目重新計算 contract_price
        // 修復：QT20260212270 等報價單「前後端開發」牌價與合約價相同、無折扣問題
        const itemsWithUnitPrice: QuoteItem[] = [];
        
        for (const item of items) {
          const catalog = getCurrentYearRate(item.service_type, item.list_price);
          let unitPrice = catalog.price || item.unit_price || item.list_price || 0;
          if (!unitPrice || unitPrice === 0) {
            unitPrice = item.contract_price || 0;
          }
          const quantity = item.quantity ?? 0;
          const want = normalizeServiceTypeKey(item.service_type);
          const contractItem = (quote.contract_id && contractItems.length > 0 && item.service_type)
            ? contractItems.find(
                (c: ContractServiceItem) => normalizeServiceTypeKey(c.service_type) === want
              )
            : undefined;
          const resolved = resolveQuoteItemRates({
            listPrice: unitPrice,
            listUnit: catalog.unit || item.unit,
            contractPrice: contractItem?.contract_price ?? item.contract_price ?? unitPrice,
            contractUnit: contractItem?.unit || item.unit,
            hasContractMatch: Boolean(contractItem) || item.contract_price != null
          });
          
          const isDeduction = item.is_deduction || false;
          const amounts = calculateLineAmounts({
            listPrice: resolved.listPrice,
            contractPrice: resolved.contractPrice,
            quantity,
            unit: resolved.unit,
            isDeduction
          });
          
          itemsWithUnitPrice.push({
            ...item,
            unit_price: resolved.listPrice,
            list_price: resolved.listPrice,
            contract_price: resolved.contractPrice,
            unit: resolved.unit,
            quantity,
            is_deduction: isDeduction,
            subtotal: amounts.subtotal,
            subtotal_discount: amounts.subtotal_discount,
            subtotal_afterdiscount: amounts.subtotal_afterdiscount
          });
        }
        
        setQuoteItemsBatch(itemsWithUnitPrice);
      }
    } catch {
      // Error loading quote items
      clearQuoteItems();
    }

    // 載入聯絡人資料
    setSelectedContacts(quote.contact_ids || []);
    setPrimaryContactId(quote.primary_contact_id || '');
  };

  const generateQuoteNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `QT${year}${month}${day}${random}`;
  };

  const handleContractChange = async (contractId: string) => {
    setCurrentQuote(prev => prev ? { ...prev, contract_id: contractId } : null);
    
    if (contractId) {
      await fetchContractServiceItems(contractId);
    } else {
      setContractServiceItems([]);
    }
  };





  // loadTemplate 函數已移除，因為未使用

  const loadDefaultTerm = (defaultTerm: DefaultTerm) => {
    if (defaultTerm.content) {
      setCurrentQuote(prev => prev ? { ...prev, terms: defaultTerm.content } : null);
    }
  };

  const handleContactSelection = (contactId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedContacts(prev => [...prev, contactId]);
    } else {
      setSelectedContacts(prev => prev.filter(id => id !== contactId));
      // 如果移除的是主要聯絡人，清空主要聯絡人
      if (primaryContactId === contactId) {
        setPrimaryContactId('');
      }
    }
  };

  const handlePrimaryContactChange = (contactId: string) => {
    setPrimaryContactId(contactId);
  };

  const getCustomerContacts = (customerId: string) => {
    return contacts.filter(contact =>
      contact.customer_id === customerId &&
      (isContactActive(contact) || selectedContacts.includes(contact.id) || contact.id === primaryContactId)
    );
  };

  const saveQuote = async () => {
    if (!currentQuote) return;

    // 驗證必填欄位
    if (!currentQuote.customer_id) {
      alert('請選擇客戶');
      return;
    }
    if (!currentQuote.quote_number.trim()) {
      alert('請輸入報價單號');
      return;
    }
    if (!currentQuote.quote_name.trim()) {
      alert('請輸入報價單名稱');
      return;
    }

    try {
      let savedQuoteId: string;
      const user = getCurrentUser();

      // 使用前端計算的總金額（不依賴資料庫觸發器）
      // 重新計算一次確保數值正確
      const totals = summarizeQuoteItemTotals(quoteItems);
      const taxRate = 5;

      // 準備要保存的報價單資料，使用前端計算的值
      // Migration 024 已執行，可以使用新欄位（subtotal_amount, subtotal_discount, subtotal_afterdiscount）
      // 這些欄位會由資料庫觸發器自動計算，但我們也提供前端計算的值作為初始值
      // 乙方資訊（專案聯絡人/Email）必須為開立報價單的人：僅新建時設定，編輯時保留原建立者
      const creatorToSave = isEditing
        ? { creator_name: currentQuote.creator_name || user?.displayName || user?.email || '', creator_email: currentQuote.creator_email || user?.email || '' }
        : { creator_name: user?.displayName || user?.email || '', creator_email: user?.email || '' };

      const quoteToSave = {
        ...currentQuote,
        // 使用前端計算的值（包含新欄位和向後兼容的欄位）
        subtotal: totals.subtotal,
        subtotal_amount: totals.subtotal,
        subtotal_discount: totals.discount,
        subtotal_afterdiscount: totals.afterDiscount,
        tax_rate: taxRate,
        tax_amount: totals.tax,
        total_amount: totals.total,
        total_discount: totals.discount,
        discounted_total: totals.afterDiscount,
        contact_ids: selectedContacts,
        primary_contact_id: primaryContactId || null,
        ...creatorToSave
      };

      if (isEditing) {
        await quotesService.update(currentQuote.id!, quoteToSave as Partial<Quote>);
        savedQuoteId = currentQuote.id!;
      } else {
        const newQuote = await quotesService.create(quoteToSave as Omit<Quote, 'id' | 'created_at' | 'updated_at'>);
        savedQuoteId = newQuote.id;
      }

      // 儲存報價項目
      if (quoteItems.length > 0) {
        const itemsToSave = quoteItems.map(item => {
          // 確保 unit_price 不為 0
          let unitPrice = item.unit_price;
          if (!unitPrice || unitPrice === 0) {
            unitPrice = getCurrentYearListPrice(item.service_type, item.list_price);
          }
          if (!unitPrice || unitPrice === 0) {
            unitPrice = item.list_price || 0;
          }
          
          // 確保 list_price 也有值
          const listPrice = item.list_price || unitPrice || 0;
          
          return {
            quote_id: savedQuoteId,
            service_type: item.service_type,
            item_name: item.item_name,
            list_price: listPrice,
            unit_price: unitPrice || listPrice, // 確保不為 0
            contract_price: item.contract_price,
            quantity: item.quantity,
            unit: item.unit || '小時',
            is_deduction: item.is_deduction || false
          };
        });

        // 先刪除現有的報價項目
        if (isEditing) {
          const existingItems = await quotesService.getQuoteItems(savedQuoteId);
          for (const item of existingItems) {
            await quotesService.deleteQuoteItem(item.id);
          }
        }

        // 新增新的報價項目，使用前端計算的值
        for (const item of itemsToSave) {
          if (item.quote_id) {
            const unitPrice = item.unit_price || item.list_price || 0;
            const rate = serviceRates.find(r => r.service_type === item.service_type);
            const isMarkup = isMarkupRate({
              unit: item.unit,
              chargeType: rate?.charge_type
            });
            if (!isMarkup && (!unitPrice || unitPrice === 0)) {
              continue;
            }
            
            const quantity = item.quantity ?? 0;
            const contractPrice = item.contract_price ?? 0;
            const isDeduction = item.is_deduction || false;
            const amounts = calculateLineAmounts({
              listPrice: unitPrice,
              contractPrice,
              quantity,
              unit: item.unit,
              isDeduction
            });
            
            await quotesService.createQuoteItem({
              quote_id: item.quote_id,
              service_type: item.service_type,
              item_name: item.item_name,
              list_price: item.list_price || unitPrice,
              unit_price: unitPrice,
              contract_price: contractPrice,
              quantity: quantity,
              unit: item.unit || '小時',
              is_deduction: isDeduction,
              subtotal: amounts.subtotal,
              subtotal_discount: amounts.subtotal_discount,
              subtotal_afterdiscount: amounts.subtotal_afterdiscount
            });
          }
        }
      }

      setShowCreateModal(false);
      setCurrentQuote(null);
      clearQuoteItems();
      fetchQuotes();
      alert('報價單儲存成功！');
    } catch (error) {
      if (error instanceof Error) {
        alert(`儲存失敗：${error.message}`);
      } else {
        alert('儲存失敗，請檢查資料是否正確');
      }
    }
  };

  const deleteQuote = async (quote: Quote) => {
    if (quote.status === 'sent' || quote.status === 'signed_back') {
      alert('報價單已寄送，不可刪除');
      return;
    }
    if (!confirm('確定要刪除此報價單嗎？')) return;
    
    try {
      await quotesService.delete(quote.id!);
      fetchQuotes();
    } catch {
      alert('刪除報價單失敗，請稍後再試');
    }
  };

  // 複製報價單
  const copyQuote = async (quote: Quote) => {
    try {
      const user = getCurrentUser();
      // 取得原報價單的明細資料
      const originalItems = await quotesService.getQuoteItems(quote.id!);

      // 建立新的報價單資料（複製為新報價單，開立者為執行複製的當前用戶）
      const newQuote: Omit<Quote, 'id' | 'created_at' | 'updated_at'> = {
        customer_id: quote.customer_id,
        project_id: quote.project_id,
        contract_id: quote.contract_id,
        quote_number: generateQuoteNumber(),
        quote_name: `${quote.quote_name} (複製)`,
        status: 'draft',
        issue_date: new Date().toISOString().slice(0, 10),
        valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10), // 30天後
        subtotal: quote.subtotal,
        tax_rate: quote.tax_rate,
        tax_amount: quote.tax_amount,
        total_amount: quote.total_amount,
        discounted_total: quote.discounted_total,
        total_discount: quote.total_discount,
        terms: quote.terms,
        contact_ids: quote.contact_ids,
        primary_contact_id: quote.primary_contact_id,
        creator_name: user?.displayName || user?.email || '',
        creator_email: user?.email || '',
        customer_name: quote.customer_name, // 添加客戶名稱
        customer_tax_id: quote.customer_tax_id
      };

      // 儲存新報價單
      const newQuoteData = await quotesService.create(newQuote);

      // 複製報價單明細
      if (originalItems && originalItems.length > 0) {
        for (const item of originalItems) {
          await quotesService.createQuoteItem({
            quote_id: newQuoteData.id,
            service_type: item.service_type,
            item_name: item.item_name,
            list_price: item.list_price,
            unit_price: item.unit_price ?? getCurrentYearListPrice(item.service_type, item.list_price),
            contract_price: item.contract_price,
            quantity: item.quantity,
            unit: item.unit || '小時',
            is_deduction: item.is_deduction || false
          });
        }
      }

      await fetchQuotes();
      alert('報價單複製成功！');
    } catch (error) {
      alert(`複製報價單失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'sent': return 'bg-primary/20 text-primary-dark';
      case 'signed_back': return 'bg-success/20 text-success';
      case 'failed': return 'bg-danger/20 text-danger';
      case 'expired': return 'bg-warning/20 text-warning';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft': return '草稿';
      case 'sent': return '已寄送';
      case 'signed_back': return '已簽回';
      case 'failed': return '失敗';
      case 'expired': return '已過期';
      default: return '未知';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return <DocumentTextIcon className="w-4 h-4" />;
      case 'sent': return <ClockIcon className="w-4 h-4" />;
      case 'signed_back': return <CheckCircleIcon className="w-4 h-4" />;
      case 'failed': return <XCircleIcon className="w-4 h-4" />;
      case 'expired': return <ExclamationTriangleIcon className="w-4 h-4" />;
      default: return <DocumentTextIcon className="w-4 h-4" />;
    }
  };

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.company : '未知客戶';
  };

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : '未知專案';
  };







  const formatCurrency = (amount: number | null | undefined) => {
    const numAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numAmount);
  };

  const isItemMarkup = (item: { service_type?: string; unit?: string }) => {
    if (isProcurementServiceType(item.service_type)) {
      return true;
    }
    const rate = serviceRates.find(r => r.service_type === item.service_type);
    return isMarkupRate({ unit: item.unit, chargeType: rate?.charge_type });
  };

  const isRateMarkup = (rate: { service_type?: string; unit?: string; charge_type?: string }) =>
    isProcurementServiceType(rate.service_type) || isMarkupRate({ unit: rate.unit, chargeType: rate.charge_type });

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    // 如果已經是 YYYY-MM-DD 格式，直接返回
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return `${year}/${month}/${day}`;
    }
    // 如果包含時間，只取日期部分
    const dateStr = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateString;
    // 只顯示日期，不包含時間
    return date.toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  // 格式化日期為檔名格式（YYYYMMDD）
  const formatDateForFileName = (dateString: string): string => {
    if (!dateString) return '';
    // 如果已經是 YYYY-MM-DD 格式，直接轉換
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString.replace(/-/g, '');
    }
    // 如果包含時間，只取日期部分
    const dateStr = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    // 格式化為 YYYYMMDD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  };

  // 取得當年度服務收費表
  const getCurrentYearServiceRates = () => {
    const currentYear = new Date().getFullYear();
    // 過濾出當年度或沒有年份標記的服務收費（視為當年度）
    return serviceRates.filter(rate => {
      const rateYear = // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (rate as any).year;
      return !rateYear || rateYear === currentYear;
    });
  };

  const indexedQuoteItems = quoteItems.map((item, index) => ({ item, index }));
  const hourQuoteRows = indexedQuoteItems.filter(({ item }) => !isItemMarkup(item));
  const markupQuoteRows = indexedQuoteItems.filter(({ item }) => isItemMarkup(item));
  const quoteAmountSummary = summarizeQuoteItemTotals(quoteItems);
  const yearServiceRates = getCurrentYearServiceRates();
  const hourServiceRates = yearServiceRates.filter(rate => !isRateMarkup(rate));
  const hourContractOptions = contractServiceItems.filter(item =>
    !isMarkupRate({ unit: item.unit }) && !isProcurementServiceType(item.service_type)
  );
  const useContractServiceOptions = Boolean(currentQuote?.contract_id && contractServiceItems.length > 0);
  const openBulkImport = () => setShowBulkImport(true);




  // 取得可用的服務類型（與新增項目邏輯保持一致）
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getAvailableServiceTypes = (): Array<{ service_type: string; unit_price?: number; unit?: string; [key: string]: any }> => {
    // 如果有合約且有合約服務項目，只使用合約服務項目
    if (currentQuote?.contract_id && contractServiceItems.length > 0) {
      return contractServiceItems.map((item: ContractServiceItem) => ({
        id: item.id,
        service_type: item.service_type,
        unit_price: item.contract_price,
        unit: item.unit,
        year: new Date().getFullYear()
      }));
    }
    
    // 如果沒有合約或合約沒有服務項目，則使用當年度服務收費表
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return getCurrentYearServiceRates() as Array<{ service_type: string; unit_price?: number; unit?: string; [key: string]: any }>;
  };

  // 批量匯入報價項目
  const handleBulkImport = () => {
    if (!bulkImportData.trim()) {
      alert('請輸入匯入資料');
      return;
    }

    try {
      const lines = bulkImportData.trim().split('\n');
      const newItems: QuoteItem[] = [];
      
      lines.forEach((line, lineIndex) => {
        const columns = line.split(',').map(col => col.trim());
        
        if (bulkImportMode === 'full') {
          // 完整模式：項目名稱,收費類別,數量
          if (columns.length !== 3) {
            throw new Error(`第 ${lineIndex + 1} 行格式錯誤，應為：項目名稱,收費類別,數量`);
          }
          
          const [itemName, serviceType, quantity] = columns;
          const quantityNum = parseFloat(quantity);
          
          if (isNaN(quantityNum) || quantityNum <= 0) {
            throw new Error(`第 ${lineIndex + 1} 行數量格式錯誤`);
          }
          
          // 使用與新增項目一致的服務類型選擇邏輯
          const availableServiceTypes = getAvailableServiceTypes();
          const wantType = normalizeServiceTypeKey(serviceType);
          const selectedRate = availableServiceTypes.find(
            (r: { service_type?: string; unit_price?: number }) =>
              normalizeServiceTypeKey(r.service_type) === wantType
          );
          const listPrice = selectedRate?.unit_price || 0;
          const listUnit = selectedRate?.unit || '小時';
          let contractStored = listPrice;
          let contractUnit = listUnit;
          let hasContractMatch = false;
          if (currentQuote?.contract_id && contractServiceItems.length > 0) {
            const cid = currentQuote.contract_id;
            const contractItem = contractServiceItems.find(item => {
              const idOk = !item.contract_id || String(item.contract_id) === String(cid);
              return idOk && normalizeServiceTypeKey(item.service_type) === wantType;
            });
            if (contractItem) {
              hasContractMatch = true;
              contractStored = contractItem.contract_price;
              contractUnit = contractItem.unit || listUnit;
            }
          }
          const resolved = resolveQuoteItemRates({
            listPrice,
            listUnit,
            contractPrice: contractStored,
            contractUnit,
            hasContractMatch
          });
          
          if (listPrice === 0) {
            throw new Error(`第 ${lineIndex + 1} 行收費類別「${serviceType}」不存在`);
          }
          
          const item: QuoteItem = {
            service_type: serviceType,
            item_name: itemName,
            list_price: resolved.listPrice,
            unit_price: resolved.listPrice,
            contract_price: resolved.contractPrice,
            quantity: quantityNum,
            unit: resolved.unit,
            is_deduction: false
          };
          
          newItems.push(item);
        } else {
          // 分類模式：項目名稱,數量
          if (columns.length !== 2) {
            throw new Error(`第 ${lineIndex + 1} 行格式錯誤，應為：項目名稱,數量`);
          }
          
          if (!selectedServiceType) {
            throw new Error('請選擇收費類別');
          }
          
          const [itemName, quantity] = columns;
          const quantityNum = parseFloat(quantity);
          
          if (isNaN(quantityNum) || quantityNum <= 0) {
            throw new Error(`第 ${lineIndex + 1} 行數量格式錯誤`);
          }
          
          // 使用與新增項目一致的服務類型選擇邏輯
          const availableServiceTypes = getAvailableServiceTypes();
          const wantSel = normalizeServiceTypeKey(selectedServiceType);
          const selectedRate = availableServiceTypes.find(
            (r: { service_type?: string; unit_price?: number }) =>
              normalizeServiceTypeKey(r.service_type) === wantSel
          );
          const listPrice = selectedRate?.unit_price || 0;
          const listUnit = selectedRate?.unit || '小時';
          let contractStored = listPrice;
          let contractUnit = listUnit;
          let hasContractMatch = false;
          if (currentQuote?.contract_id && contractServiceItems.length > 0) {
            const cid = currentQuote.contract_id;
            const contractItem = contractServiceItems.find(item => {
              const idOk = !item.contract_id || String(item.contract_id) === String(cid);
              return idOk && normalizeServiceTypeKey(item.service_type) === wantSel;
            });
            if (contractItem) {
              hasContractMatch = true;
              contractStored = contractItem.contract_price;
              contractUnit = contractItem.unit || listUnit;
            }
          }
          const resolved = resolveQuoteItemRates({
            listPrice,
            listUnit,
            contractPrice: contractStored,
            contractUnit,
            hasContractMatch
          });
          
          const item: QuoteItem = {
            service_type: selectedServiceType,
            item_name: itemName,
            list_price: resolved.listPrice,
            unit_price: resolved.listPrice,
            contract_price: resolved.contractPrice,
            quantity: quantityNum,
            unit: resolved.unit,
            is_deduction: false
          };
          
          newItems.push(item);
        }
      });
      
      setQuoteItemsBatch([...quoteItems, ...newItems]);
      calculateTotals([...quoteItems, ...newItems]);
      setBulkImportData('');
      setShowBulkImport(false);
      alert(`成功匯入 ${newItems.length} 個項目`);
    } catch (error) {
      alert(error instanceof Error ? error.message : '匯入失敗');
    }
  };

  // 下載 PDF 處理函數
  const handleDownloadPdf = async (quote?: Quote) => {
    const targetQuote = quote || currentQuote;
    if (!targetQuote) return;

    // 取得主要聯絡人資訊
    let contactName = '';
    let contactEmail = '';
    if (targetQuote.primary_contact_id) {
      const primaryContact = contacts.find(c => c.id === targetQuote.primary_contact_id);
      if (primaryContact) {
        contactName = primaryContact.name;
        contactEmail = primaryContact.email;
      }
    }

    // 取得報價單明細資料
    let quoteItemsData: Array<{
      item_name: string;
      service_type: string;
      list_price: number;
      contract_price: number;
      unit: string;
      quantity: number;
      subtotal: number;
      discount_amount: number;
      discounted_total: number;
    }> = [];
    if (quote) {
      // 如果是從列表下載，需要從資料庫取得明細，並依合約重新計算牌價與合約價
      try {
        const itemsData = await quotesService.getQuoteItems(quote.id!);
        if (itemsData && itemsData.length > 0) {
          quoteItemsData = await buildQuoteItemsForPdf(itemsData, quote);
        }
      } catch {
        // 靜默處理錯誤
      }
    } else {
      // 從編輯模式下載：使用當前明細，依合約重新計算牌價與合約價（邏輯一致）
      quoteItemsData = await buildQuoteItemsForPdf(quoteItems, currentQuote);
    }

    // 取得客戶統一編號
    const customer = customers.find(c => c.id === targetQuote.customer_id);
    const customerTaxId = customer?.tax_id || '';

    // 依 recalculated 明細重新計算總計（確保與 PDF 明細一致）
    const pdfTotals = summarizeQuoteItemTotals(quoteItemsData);

    // 組合 PDF 需要的資料
    const pdfData: PdfQuoteData = {
      quote_number: targetQuote.quote_number,
      project_name: targetQuote.quote_name,
      company_name: '遠程資科有限公司',
      company_tax_id: '83074822',
      company_address: '104台北市中山區長安東路二段81號11樓之4',
      customer_name: customer?.company || '',
      customer_tax_id: customerTaxId,
      contact_name: contactName,
      contact_email: contactEmail,
      creator_name: targetQuote.creator_name || '',
      creator_email: targetQuote.creator_email || '',
      issue_date: targetQuote.issue_date,
      valid_until: targetQuote.valid_until,
      items: quoteItemsData,
      subtotal: pdfTotals.subtotal,
      subtotal_amount: pdfTotals.subtotal,
      total_discount: pdfTotals.discount,
      subtotal_discount: pdfTotals.discount,
      discounted_total: pdfTotals.afterDiscount,
      subtotal_afterdiscount: pdfTotals.afterDiscount,
      tax_amount: pdfTotals.tax,
      total_amount: pdfTotals.total,
      terms: targetQuote.terms || '',
      serviceRates: serviceRates.map(rate => // eslint-disable-next-line @typescript-eslint/no-explicit-any
 ({
        service_type: rate.service_type,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        unit_price: (rate as any).unit_price || 0,
        unit: // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (rate as any).unit || '小時',
        year: // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (rate as any).year || new Date().getFullYear()
      }))
    };

    // 匯入 Stamp base64 資料
    const dateStr = formatDateForFileName(targetQuote.issue_date);
    const fileName = `${targetQuote.quote_number}-${targetQuote.quote_name}-${dateStr}.pdf`;
    import('../lib/stampBase64').then((module) => {
      try {
        downloadQuotePdf(pdfData, fileName, module.default);
      } catch {
        alert('PDF 產生失敗，請稍後再試');
      }
    }).catch(() => {
      // 如果無法載入 Stamp，使用預設下載
      try {
        downloadQuotePdf(pdfData, fileName);
      } catch {
        alert('PDF 產生失敗，請稍後再試');
      }
    });
  };

  // 重新生成 PDF 處理函數
  const handleRegeneratePdf = async (quote: Quote) => {
    if (!quote) return;

    try {
      // 取得主要聯絡人資訊
      let contactName = '';
      let contactEmail = '';
      if (quote.primary_contact_id) {
        const primaryContact = contacts.find(c => c.id === quote.primary_contact_id);
        if (primaryContact) {
          contactName = primaryContact.name;
          contactEmail = primaryContact.email;
        }
      }

      // 取得報價單明細資料，依合約重新計算牌價與合約價（牌價=當年度服務收費表，合約價<=牌價）
      const itemsData = await quotesService.getQuoteItems(quote.id!);
      if (!itemsData || itemsData.length === 0) {
        alert('報價單沒有明細項目，無法生成 PDF');
        return;
      }
      const quoteItemsData = await buildQuoteItemsForPdf(itemsData, quote);
      if (quoteItemsData.length === 0) {
        alert('報價單沒有可用的明細項目，無法生成 PDF');
        return;
      }

      // 取得客戶統一編號
      const customer = customers.find(c => c.id === quote.customer_id);
      const customerTaxId = customer?.tax_id || '';

      // 重新計算金額
      const pdfTotals = summarizeQuoteItemTotals(quoteItemsData);
      const recalculatedSubtotal = pdfTotals.subtotal;
      const recalculatedTotalDiscount = pdfTotals.discount;
      const recalculatedDiscountedTotal = pdfTotals.afterDiscount;
      const recalculatedTaxAmount = pdfTotals.tax;
      const recalculatedTotalAmount = pdfTotals.total;

      // 組合 PDF 需要的資料
      const pdfData: PdfQuoteData = {
        quote_number: quote.quote_number,
        project_name: quote.quote_name,
        company_name: '遠程資科有限公司',
        company_tax_id: '83074822',
        company_address: '104台北市中山區長安東路二段81號11樓之4',
        customer_name: customer?.company || '',
        customer_tax_id: customerTaxId,
        contact_name: contactName,
        contact_email: contactEmail,
        creator_name: quote.creator_name || '',
        creator_email: quote.creator_email || '',
        issue_date: quote.issue_date,
        valid_until: quote.valid_until,
        items: quoteItemsData,
        subtotal: recalculatedSubtotal,
        subtotal_amount: recalculatedSubtotal,
        total_discount: recalculatedTotalDiscount,
        subtotal_discount: recalculatedTotalDiscount,
        discounted_total: recalculatedDiscountedTotal,
        subtotal_afterdiscount: recalculatedDiscountedTotal,
        tax_amount: recalculatedTaxAmount,
        total_amount: recalculatedTotalAmount,
        terms: quote.terms || '',
        serviceRates: serviceRates.map(rate => // eslint-disable-next-line @typescript-eslint/no-explicit-any
 ({
          service_type: rate.service_type,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          unit_price: (rate as any).unit_price || 0,
          unit: // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (rate as any).unit || '小時',
          year: // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (rate as any).year || new Date().getFullYear()
        }))
      };

      // 載入印章並生成 PDF blob
      const dateStr = formatDateForFileName(quote.issue_date);
      const fileName = `${quote.quote_number}-${quote.quote_name}-${dateStr}.pdf`;
      import('../lib/stampBase64').then((module) => {
        try {
          downloadQuotePdf(pdfData, fileName, module.default);
          alert('PDF 重新生成成功！');
        } catch {
          alert('PDF 產生失敗，請稍後再試');
        }
      }).catch(() => {
        try {
          downloadQuotePdf(pdfData, fileName);
          alert('PDF 重新生成成功！');
        } catch {
          alert('PDF 產生失敗，請稍後再試');
        }
      });
    } catch (error) {
      alert(error instanceof Error ? `重新生成 PDF 失敗：${error.message}` : '重新生成 PDF 失敗，請稍後再試');
    }
  };

  // 開啟狀態修改模態框
  const openStatusModal = (quote: Quote) => {
    setStatusQuote(quote);
    setNewStatus(quote.status);
    setSignedBackFileUrl(quote.signed_back_file_url || '');
    setShowStatusModal(true);
  };

  // 儲存狀態修改
  const saveStatusChange = async () => {
    if (!statusQuote) return;

    // 驗證已簽回狀態需要連結
    if (newStatus === 'signed_back' && !signedBackFileUrl.trim()) {
      alert('已簽回狀態需要填入簽回連結');
      return;
    }

    try {
      const updateData: Partial<Quote> = {
        status: newStatus as Quote['status'],
        updated_at: new Date().toISOString()
      };

      // 如果是已簽回狀態，添加連結與簽回日期（僅首次簽回時寫入，避免編輯連結時覆蓋）
      if (newStatus === 'signed_back') {
        updateData.signed_back_file_url = signedBackFileUrl.trim();
        if (statusQuote.status !== 'signed_back') {
          updateData.signed_back_date = new Date().toISOString().split('T')[0];
        }
      }

      await quotesService.update(statusQuote.id!, updateData);

      setShowStatusModal(false);
      setStatusQuote(null);
      setNewStatus('');
      setSignedBackFileUrl('');
      fetchQuotes();
      alert('狀態更新成功！');
    } catch {
      alert('狀態更新失敗，請稍後再試');
    }
  };

  // 開啟發送郵件模態框
  const openEmailModal = async (quote: Quote) => {
    setEmailQuote(quote);
    
    // 取得主要聯絡人資訊
    let contactEmail = '';
    let contactName = '';
    let contactPhone = '';
    let contactPosition = '';
    if (quote.primary_contact_id) {
      const primaryContact = contacts.find(c => c.id === quote.primary_contact_id);
      if (primaryContact) {
        contactEmail = primaryContact.email;
        contactName = primaryContact.name;
        contactPhone = primaryContact.phone;
        contactPosition = primaryContact.position;
      }
    }
    
    // 取得郵件模板（只載入報價單類別）
    try {
      const templates = await getEmailTemplates('quote');
      setEmailTemplates(templates || []);
      if (templates && templates.length > 0) {
        setSelectedTemplate(templates[0]);
      }
    } catch {
      // 載入郵件模板失敗
    }
    
    const selectedCustomer = customers.find(c => c.id === quote.customer_id);
    const amountFormatted = formatEmailAmount(quote.total_amount);
    const validUntilDateOnly = formatDate(quote.valid_until);
    const variables = {
      quote_number: quote.quote_number,
      quote_name: quote.quote_name,
      customer_name: selectedCustomer?.company || '',
      total_amount: amountFormatted,
      金額: amountFormatted,
      valid_until: validUntilDateOnly,
      contact_name: contactName,
      contact_email: contactEmail,
      contact_phone: contactPhone,
      contact_position: contactPosition,
      recipient_name: contactName
    };
    
    const processedSubject = replaceTemplateVariables(selectedTemplate?.subject || '', variables);
    const processedBody = replaceTemplateVariables(selectedTemplate?.body || '', variables);
    
    setEmailData({
      recipientEmail: contactEmail,
      recipientName: contactName,
      subject: processedSubject,
      body: processedBody
    });
    
    setShowEmailModal(true);
    
    // 自動載入 PDF 預覽
    setTimeout(() => {
      handlePreviewPdf();
    }, 500);
  };

  // 處理模板選擇
  const handleTemplateChange = (template: {
    id: string;
    name: string;
    type: string;
    subject: string;
    body: string;
    variables?: string[];
  } | null) => {
    setSelectedTemplate(template);
    if (template && emailQuote) {
      const customer = customers.find(c => c.id === emailQuote.customer_id);
      
      // 取得主要聯絡人資訊
      let contactEmail = '';
      let contactName = '';
      let contactPhone = '';
      let contactPosition = '';
      if (emailQuote.primary_contact_id) {
        const primaryContact = contacts.find(c => c.id === emailQuote.primary_contact_id);
        if (primaryContact) {
          contactEmail = primaryContact.email;
          contactName = primaryContact.name;
          contactPhone = primaryContact.phone;
          contactPosition = primaryContact.position;
        }
      }
      
      const amountFormatted = formatEmailAmount(emailQuote.total_amount);
      const validUntilDateOnly = formatDate(emailQuote.valid_until);
      const variables = {
        quote_number: emailQuote.quote_number,
        quote_name: emailQuote.quote_name,
        customer_name: customer?.company || '',
        total_amount: amountFormatted,
        金額: amountFormatted,
        valid_until: validUntilDateOnly,
        contact_name: contactName,
        contact_email: contactEmail,
        contact_phone: contactPhone,
        contact_position: contactPosition,
        recipient_name: emailData.recipientName
      };
      
      const processedSubject = replaceTemplateVariables(template.subject, variables);
      const processedBody = replaceTemplateVariables(template.body, variables);
      
      setEmailData({
        ...emailData,
        subject: processedSubject,
        body: processedBody
      });
    }
  };

  // 預覽 PDF
  const handlePreviewPdf = async () => {
    if (!emailQuote) return;
    
    try {
      // 取得報價單明細，依合約重新計算牌價與合約價（牌價=當年度服務收費表，合約價<=牌價）
      const itemsData = await quotesService.getQuoteItems(emailQuote.id!);
      const quoteItemsData = await buildQuoteItemsForPdf(itemsData, emailQuote);

      // 取得客戶資訊
      const customer = customers.find(c => c.id === emailQuote.customer_id);
      const customerTaxId = customer?.tax_id || '';

      // 依 recalculated 明細重新計算總計（與 PDF 明細一致）
      const pdfTotals = summarizeQuoteItemTotals(quoteItemsData);
      const recalculatedSubtotal = pdfTotals.subtotal;
      const recalculatedTotalDiscount = pdfTotals.discount;
      const recalculatedDiscountedTotal = pdfTotals.afterDiscount;
      const recalculatedTaxAmount = pdfTotals.tax;
      const recalculatedTotalAmount = pdfTotals.total;

      // 組合 PDF 需要的資料
      const pdfData: PdfQuoteData = {
        quote_number: emailQuote.quote_number,
        project_name: emailQuote.quote_name,
        company_name: '遠程資科有限公司',
        company_tax_id: '83074822',
        company_address: '104台北市中山區長安東路二段81號11樓之4',
        customer_name: customer?.company || '',
        customer_tax_id: customerTaxId,
        contact_name: emailData.recipientName,
        contact_email: emailData.recipientEmail,
        creator_name: emailQuote.creator_name || '',
        creator_email: emailQuote.creator_email || '',
        issue_date: emailQuote.issue_date,
        valid_until: emailQuote.valid_until,
        items: quoteItemsData,
        subtotal: recalculatedSubtotal,
        total_discount: recalculatedTotalDiscount,
        discounted_total: recalculatedDiscountedTotal,
        tax_amount: recalculatedTaxAmount,
        total_amount: recalculatedTotalAmount,
        terms: emailQuote.terms || '',
        serviceRates: serviceRates.map(rate => // eslint-disable-next-line @typescript-eslint/no-explicit-any
 ({
          service_type: rate.service_type,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          unit_price: (rate as any).unit_price || 0,
          unit: // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (rate as any).unit || '小時',
          year: // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (rate as any).year || new Date().getFullYear()
        }))
      };

      // 載入印章並生成 PDF blob
      try {
        const stampModule = await import('../lib/stampBase64');
        const pdfBlob = await generateQuotePdfBlob(pdfData, stampModule.default);
        const pdfUrl = URL.createObjectURL(pdfBlob);
        setPdfPreviewUrl(pdfUrl);
        setShowPdfPreview(true);
      } catch {
        try {
          const pdfBlob = await generateQuotePdfBlob(pdfData);
          const pdfUrl = URL.createObjectURL(pdfBlob);
          setPdfPreviewUrl(pdfUrl);
          setShowPdfPreview(true);
        } catch (noStampError) {
          alert(`生成 PDF 預覽失敗: ${noStampError instanceof Error ? noStampError.message : '未知錯誤'}`);
        }
      }
    } catch (error) {
      alert(`生成 PDF 預覽失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    }
  };

  // 發送報價單郵件
  const handleSendQuoteEmail = async () => {
    if (!emailQuote || !emailData.recipientEmail) {
      alert('請填寫收件人郵件地址');
      return;
    }

    setIsSendingEmail(true);

    try {
      // 取得報價單明細，依合約重新計算牌價與合約價（牌價=當年度服務收費表，合約價<=牌價）
      const itemsData = await quotesService.getQuoteItems(emailQuote.id!);
      const quoteItemsData = await buildQuoteItemsForPdf(itemsData, emailQuote);

      // 取得客戶資訊
      const customer = customers.find(c => c.id === emailQuote.customer_id);
      const customerTaxId = customer?.tax_id || '';

      // 重新計算小計和折扣總金額
      const pdfTotals = summarizeQuoteItemTotals(quoteItemsData);

      // 組合 PDF 需要的資料
      const pdfData: PdfQuoteData = {
        quote_number: emailQuote.quote_number,
        project_name: emailQuote.quote_name,
        company_name: '遠程資科有限公司',
        company_tax_id: '83074822',
        company_address: '104台北市中山區長安東路二段81號11樓之4',
        customer_name: customer?.company || '',
        customer_tax_id: customerTaxId,
        contact_name: emailData.recipientName,
        contact_email: emailData.recipientEmail,
        creator_name: emailQuote.creator_name || '',
        creator_email: emailQuote.creator_email || '',
        issue_date: emailQuote.issue_date,
        valid_until: emailQuote.valid_until,
        items: quoteItemsData,
        subtotal: pdfTotals.subtotal,
        total_discount: pdfTotals.discount,
        discounted_total: pdfTotals.afterDiscount,
        tax_amount: pdfTotals.tax,
        total_amount: pdfTotals.total,
        terms: emailQuote.terms || '',
        serviceRates: serviceRates.map(rate => // eslint-disable-next-line @typescript-eslint/no-explicit-any
 ({
          service_type: rate.service_type,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          unit_price: (rate as any).unit_price || 0,
          unit: // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (rate as any).unit || '小時',
          year: // eslint-disable-next-line @typescript-eslint/no-explicit-any
 (rate as any).year || new Date().getFullYear()
        }))
      };

      // 載入印章並生成 PDF blob
      let pdfBlob: Blob;
      try {
        const stampModule = await import('../lib/stampBase64');
        pdfBlob = await generateQuotePdfBlob(pdfData, stampModule.default);
      } catch {
        pdfBlob = await generateQuotePdfBlob(pdfData);
      }
      
      // 上傳 PDF 到 Firebase Storage
      const dateStr = formatDateForFileName(emailQuote.issue_date);
      const fileName = `${emailQuote.quote_number}-${emailQuote.quote_name}-${dateStr}.pdf`;
      const storageRef = ref(storage, `pdfs/${fileName}`);
      
      try {
        await uploadBytes(storageRef, pdfBlob);
      } catch (uploadError) {
        const errorMessage = uploadError instanceof Error ? uploadError.message : '未知錯誤';
        throw new Error(`PDF 上傳失敗: ${errorMessage}`);
      }
      
      let pdfUrl: string;
      try {
        pdfUrl = await getDownloadURL(storageRef);
      } catch (urlError) {
        const errorMessage = urlError instanceof Error ? urlError.message : '未知錯誤';
        throw new Error(`取得 PDF 連結失敗: ${errorMessage}`);
      }


      
      // 取得其他聯絡人的郵件地址（排除主要聯絡人）
      const otherContactEmails: string[] = [];
      if (emailQuote.contact_ids && emailQuote.contact_ids.length > 0) {
        emailQuote.contact_ids.forEach(contactId => {
          if (contactId !== emailQuote.primary_contact_id) {
            const contact = contacts.find(c => c.id === contactId);
            if (contact && contact.email) {
              otherContactEmails.push(contact.email);
            }
          }
        });
      }
      

      
      // 發送郵件
      await sendQuoteEmail({
        quoteData: {
          quote_number: emailQuote.quote_number,
          quote_name: emailQuote.quote_name,
          customer_name: customer?.company || '',
          total_amount: emailQuote.total_amount,
          valid_until: emailQuote.valid_until
        },
        recipientEmail: emailData.recipientEmail,
        recipientName: emailData.recipientName,
        pdfUrl,
        subject: emailData.subject,
        body: emailData.body,
        ccEmails: otherContactEmails
      });

      // 郵件發送成功後，自動將報價單狀態改為「已寄送」
      if (emailQuote.id) {
        try {
          await quotesService.update(emailQuote.id, { 
            status: 'sent'
          });
          // 更新本地狀態
          setQuotes(prevQuotes => 
            prevQuotes.map(quote => 
              quote.id === emailQuote.id 
                ? { ...quote, status: 'sent' as const }
                : quote
            )
          );
        } catch {
          // 更新狀態失敗，但不影響郵件發送
        }
      }

      alert('郵件發送成功！報價單狀態已自動更新為「已寄送」');
      setShowEmailModal(false);
      setEmailQuote(null);
      setEmailData({ recipientEmail: '', recipientName: '', subject: '', body: '' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '未知錯誤';
      alert(`發送郵件失敗: ${errorMessage}`);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const filteredQuotes = quotes.filter(quote => {
    if (selectedCustomerFilter && quote.customer_id !== selectedCustomerFilter) return false;
    if (selectedStatusFilter && quote.status !== selectedStatusFilter) return false;
    return true;
  });

  // 統計卡片僅套用客戶篩選（不套用狀態篩選），呈現該範圍的總金額
  const quotesForStats = quotes.filter(quote =>
    !selectedCustomerFilter || quote.customer_id === selectedCustomerFilter
  );

  // 發出的報價單總金額：已寄送 + 已簽回（含稅金額，整數）
  const issuedTotalAmount = Math.round(
    quotesForStats
      .filter(quote => quote.status === 'sent' || quote.status === 'signed_back')
      .reduce((sum, quote) => {
        const amount = quote.total_amount ?? quote.discounted_total ?? 0;
        const numAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
        return sum + numAmount;
      }, 0)
  );

  // 已簽回的報價單總金額（含稅金額，整數）
  const signedTotalAmount = Math.round(
    quotesForStats
      .filter(quote => quote.status === 'signed_back')
      .reduce((sum, quote) => {
        const amount = quote.total_amount ?? quote.discounted_total ?? 0;
        const numAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
        return sum + numAmount;
      }, 0)
  );
  
  // 已簽回的報價單數量（僅套用客戶篩選）
  const signedTotalCount = quotesForStats.filter(quote => quote.status === 'signed_back').length;

  // 成交率：已簽回數量 / 報價單總數
  const conversionRate = quotesForStats.length > 0
    ? Math.round((signedTotalCount / quotesForStats.length) * 100)
    : 0;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-ci-text">報價單管理</h1>
        <button
          onClick={createNewQuote}
          className="bg-primary-dark text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-darker"
        >
          <PlusIcon className="w-5 h-5" />
          新增報價單
        </button>
      </div>

      {/* 統計卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <CurrencyDollarIcon className="w-8 h-8 text-ci-highlight" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">發出的報價單總金額</p>
              <p className="text-lg font-semibold text-ci-text">{formatCurrency(issuedTotalAmount)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="w-8 h-8 text-ci-button" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">已簽回的報價單總金額</p>
              <p className="text-lg font-semibold text-ci-text">{formatCurrency(signedTotalAmount)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <ClockIcon className="w-8 h-8 text-ci-highlight" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">報價單總數</p>
              <p className="text-lg font-semibold text-ci-text">{quotesForStats.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <DocumentTextIcon className="w-8 h-8 text-ci-button" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">已簽回報價單數量</p>
              <p className="text-lg font-semibold text-ci-text">{signedTotalCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <ChartBarIcon className="w-8 h-8 text-primary" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">成交率</p>
              <p className="text-lg font-semibold text-ci-text">
                {signedTotalCount} / {quotesForStats.length} ({conversionRate}%)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 篩選器 */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">客戶篩選</label>
            <select
              value={selectedCustomerFilter}
              onChange={(e) => setSelectedCustomerFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">全部客戶</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.company}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">狀態篩選</label>
            <select
              value={selectedStatusFilter}
              onChange={(e) => setSelectedStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">全部狀態</option>
              <option value="draft">草稿</option>
              <option value="sent">已寄送</option>
              <option value="signed_back">已簽回</option>
              <option value="failed">失敗</option>
              <option value="expired">已過期</option>
            </select>
          </div>
        </div>
      </div>

      {/* 報價單列表 */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-primary/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-dark uppercase tracking-wider">
                    報價單資訊
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-dark uppercase tracking-wider">
                    客戶
                  </th>

                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-dark uppercase tracking-wider">
                    金額
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-dark uppercase tracking-wider">
                    狀態
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-dark uppercase tracking-wider">
                    日期
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-dark uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredQuotes.map((quote) => (
                  <tr key={quote.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {quote.quote_number}
                        </div>
                        <div className="text-sm text-gray-500">
                          {quote.quote_name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {getCustomerName(quote.customer_id)}
                      </div>
                      {quote.project_id && (
                        <div className="text-sm text-gray-500">
                          {getProjectName(quote.project_id)}
                        </div>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(Math.round(quote.total_amount ?? quote.discounted_total ?? 0))}
                      </div>
                      <div className="text-sm text-gray-500">
                        稅率: {quote.tax_rate}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => openStatusModal(quote)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(quote.status)} hover:opacity-80 cursor-pointer`}
                        title="點擊修改狀態"
                      >
                        {getStatusIcon(quote.status)}
                        <span className="ml-1">{getStatusText(quote.status)}</span>
                      </button>
                      {quote.signed_back_file_url && (
                        <div className="mt-1 text-xs">
                          <a 
                            href={quote.signed_back_file_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary-dark hover:underline"
                          >
                            查看簽回文件
                          </a>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        開立: {formatDate(quote.issue_date)}
                      </div>
                      <div className="text-sm text-gray-500">
                        有效: {formatDate(quote.valid_until)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => editQuote(quote)}
                          className="text-primary hover:text-primary-dark"
                          title="編輯"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => copyQuote(quote)}
                          className="text-primary hover:text-primary-dark"
                          title="複製報價單"
                        >
                          <ClipboardDocumentIcon className="w-4 h-4" />
                        </button>
                        {quote.status !== 'sent' && quote.status !== 'signed_back' && (
                        <button
                          onClick={() => deleteQuote(quote)}
                          className="text-red-600 hover:text-red-900"
                          title="刪除"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                        )}
                        <button
                          onClick={() => handleDownloadPdf(quote)}
                          className="text-primary hover:text-primary-dark"
                          title="下載 PDF"
                        >
                          <DocumentTextIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRegeneratePdf(quote)}
                          className="text-primary hover:text-primary-dark"
                          title="重新生成 PDF"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                        <button
                          onClick={() => openEmailModal(quote)}
                          className="text-primary hover:text-primary-dark"
                          title="寄送郵件"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 新增/編輯報價單模態框 */}
      {showCreateModal && currentQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">
                {isEditing ? '編輯報價單' : '新增報價單'}
              </h2>
              <div className="flex items-center space-x-2">
                {/* 下載 PDF 按鈕已移除 */}
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="ml-2 text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="space-y-8">
              {/* 基本資訊區塊 */}
              <div className="bg-primary/5 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-primary-dark mb-4">基本資訊</h4>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">報價單號 *</label>
                    <input
                      type="text"
                      required
                      value={currentQuote.quote_number}
                      onChange={(e) => setCurrentQuote({...currentQuote, quote_number: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">客戶 *</label>
                    <select
                      required
                      value={currentQuote.customer_id}
                      onChange={(e) => setCurrentQuote({...currentQuote, customer_id: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    >
                      <option value="">選擇客戶</option>
                      {customers.map(customer => (
                        <option key={customer.id} value={customer.id}>
                          {customer.company}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">合約 (可選)</label>
                    <select
                      value={currentQuote.contract_id || ''}
                      onChange={(e) => handleContractChange(e.target.value)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    >
                      <option value="">選擇合約</option>
                      {contracts
                        .filter(contract => contract.customer_id === currentQuote.customer_id)
                        .map(contract => (
                          <option key={contract.id} value={contract.id}>
                            {contract.contract_number || contract.title}
                          </option>
                        ))}
                    </select>
                    {currentQuote.contract_id && (
                      <div className="mt-2 text-xs text-gray-600">
                        已載入合約服務項目: {contractServiceItems.length} 項
                        {contractServiceItems.length > 0 && (
                          <div className="mt-1">
                            {contractServiceItems.map(item => (
                              <div key={item.id} className="text-xs">
                                {item.service_type}: {formatUnitPriceDisplay(item.contract_price, item.unit)}
                                {item.unit && !isMarkupRateUnit(item.unit) && `/${item.unit}`}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">報價單名稱 *</label>
                    <input
                      type="text"
                      required
                      value={currentQuote.quote_name}
                      onChange={(e) => setCurrentQuote({...currentQuote, quote_name: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">專案</label>
                    <select
                      value={currentQuote.project_id || ''}
                      onChange={(e) => setCurrentQuote({...currentQuote, project_id: e.target.value || undefined})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    >
                      <option value="">選擇專案</option>
                      {projects
                        .filter(project => project.customer_id === currentQuote.customer_id)
                        .map(project => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* 聯絡人選擇區塊 */}
              <div className="bg-warning/10 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-warning mb-4">專案聯絡人</h4>
                {currentQuote.customer_id ? (
                  <div className="space-y-4">
                    {/* 聯絡人選擇 */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          選擇聯絡人
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            const customerContacts = getCustomerContacts(currentQuote.customer_id);
                            if (selectedContacts.length === customerContacts.length) {
                              // 如果已全選，則取消全選
                              setSelectedContacts([]);
                              setPrimaryContactId('');
                            } else {
                              // 如果未全選，則全選
                              const allContactIds = customerContacts.map(c => c.id);
                              setSelectedContacts(allContactIds);
                              // 如果沒有主要聯絡人，自動選擇第一個
                              if (!primaryContactId && allContactIds.length > 0) {
                                setPrimaryContactId(allContactIds[0]);
                              }
                            }
                          }}
                          className="text-sm text-primary hover:text-primary-dark font-medium"
                        >
                          {(() => {
                            const customerContacts = getCustomerContacts(currentQuote.customer_id);
                            return selectedContacts.length === customerContacts.length ? '取消全選' : '全選';
                          })()}
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3">
                        {getCustomerContacts(currentQuote.customer_id).map((contact) => (
                          <div key={contact.id} className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              id={`contact-${contact.id}`}
                              checked={selectedContacts.includes(contact.id)}
                              onChange={(e) => handleContactSelection(contact.id, e.target.checked)}
                              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                            />
                            <label htmlFor={`contact-${contact.id}`} className="flex-1 cursor-pointer">
                              <div className="text-sm font-medium text-gray-900">
                                {contact.name}
                                {!isContactActive(contact) && (
                                  <span className="ml-2 text-xs text-gray-500">（離職）</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">
                                {contact.position} • {contact.email}
                              </div>
                              <div className="text-xs text-gray-400">
                                {/* 聯絡人標籤已移除 */}
                              </div>
                            </label>
                          </div>
                        ))}
                        {getCustomerContacts(currentQuote.customer_id).length === 0 && (
                          <div className="col-span-2 text-center py-4">
                            <p className="text-gray-500 mb-3">此客戶尚未建立聯絡人資料</p>
                            <Link
                              to="/admin/contacts"
                              className="inline-flex items-center px-3 py-1.5 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark"
                            >
                              <PlusIcon className="h-4 w-4 mr-1" />
                              新增聯絡人
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 主要聯絡人選擇 */}
                    {selectedContacts.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          主要聯絡人
                        </label>
                        <select
                          value={primaryContactId}
                          onChange={(e) => handlePrimaryContactChange(e.target.value)}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                        >
                          <option value="">選擇主要聯絡人</option>
                          {selectedContacts.map(contactId => {
                            const contact = contacts.find(c => c.id === contactId);
                            return contact ? (
                              <option key={contact.id} value={contact.id}>
                                {contact.name} - {contact.position}
                              </option>
                            ) : null;
                          })}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          主要聯絡人將作為報價單的主要收件人
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-4">
                    請先選擇客戶以查看聯絡人選項
                  </div>
                )}
              </div>

              {/* 報價單明細區塊 */}
              <QuoteItemsEditor
                hourRows={hourQuoteRows}
                markupRows={markupQuoteRows}
                useContractOptions={useContractServiceOptions}
                hourContractItems={hourContractOptions}
                hourRates={hourServiceRates}
                currentContractId={currentQuote?.contract_id}
                serviceRates={serviceRates}
                contractServiceItems={contractServiceItems}
                formatCurrency={formatCurrency}
                onAddHourItem={addQuoteItem}
                onAddMarkupItem={addMarkupQuoteItem}
                onOpenBulkImport={openBulkImport}
                updateQuoteItem={updateQuoteItem}
                updateItemPrices={updateItemPrices}
                removeQuoteItem={removeQuoteItem}
              />

              {/* 金額計算區塊 - 使用 calculateTotals 結果，與 PDF 邏輯一致 */}
              <div className="bg-warning/10 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-warning mb-4">金額計算</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">工時類別合計</label>
                    <div className="mt-1 text-lg font-semibold text-gray-900">
                      {formatCurrency(quoteAmountSummary.hoursAfterDiscount)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">合約單價 × 數量</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">採購類別合計</label>
                    <div className="mt-1 text-lg font-semibold text-gray-900">
                      {formatCurrency(quoteAmountSummary.procurementAfterDiscount)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">實際採購金額 × 收費比例</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">折扣後總金額</label>
                    <div className="mt-1 text-lg font-semibold text-gray-900">
                      {formatCurrency(quoteAmountSummary.afterDiscount)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">工時類別＋採購類別</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">稅金 (5%)</label>
                    <div className="mt-1 text-lg font-semibold text-gray-900">
                      {formatCurrency(quoteAmountSummary.tax)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">折扣後總金額 × 5%</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">含稅總金額</label>
                    <div className="mt-1 text-lg font-semibold text-primary">
                      {formatCurrency(quoteAmountSummary.total)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">工時類別＋採購類別＋稅金</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">折扣總金額</label>
                    <div className="mt-1 text-lg font-semibold text-gray-900">
                      {formatCurrency(quoteAmountSummary.discount)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">僅工時項目的牌價與合約價差額</div>
                  </div>
                </div>
              </div>

              {/* 條款區塊 */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-sm font-medium text-purple-900">條款與注意事項</h4>
                  <div className="flex space-x-2">
                    {/* 條款選擇下拉選單 */}
                    {defaultTerms.length > 0 && (
                      <select
                        onChange={(e) => {
                          const defaultTerm = defaultTerms.find(t => t.id === e.target.value);
                          if (defaultTerm) {
                            loadDefaultTerm(defaultTerm);
                            alert(`已載入「${defaultTerm.name}」條款`);
                          }
                        }}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <option value="">選擇特定條款</option>
                        {defaultTerms.map(term => (
                          <option key={term.id} value={term.id}>
                            {term.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
                <textarea
                  value={currentQuote.terms}
                  onChange={(e) => setCurrentQuote({...currentQuote, terms: e.target.value})}
                  rows={6}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                  placeholder="輸入付款條件與條款，或點擊上方按鈕載入預設條款..."
                />
                <div className="mt-2 flex justify-between items-center">
                  <p className="text-xs text-gray-500">
                    可輸入付款條件、交貨方式、保固條款等相關條款內容
                  </p>
                  <div className="text-xs text-gray-400">
                    可用條款: {defaultTerms.length} 個
                  </div>
                </div>
              </div>

              {/* 日期與狀態區塊 */}
              <div className="bg-success/10 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-success mb-4">日期與狀態</h4>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">開立日期 *</label>
                    <input
                      type="date"
                      required
                      value={currentQuote.issue_date ? (() => {
                        // 確保日期格式為 YYYY-MM-DD
                        const dateStr = currentQuote.issue_date;
                        if (dateStr.includes('T')) {
                          return dateStr.split('T')[0];
                        }
                        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                          return dateStr;
                        }
                        const date = new Date(dateStr);
                        if (!isNaN(date.getTime())) {
                          return date.toISOString().split('T')[0];
                        }
                        return dateStr;
                      })() : ''}
                      onChange={(e) => setCurrentQuote({...currentQuote, issue_date: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">有效期限 *</label>
                    <input
                      type="date"
                      required
                      value={currentQuote.valid_until ? (() => {
                        // 確保日期格式為 YYYY-MM-DD
                        const dateStr = currentQuote.valid_until;
                        if (dateStr.includes('T')) {
                          return dateStr.split('T')[0];
                        }
                        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                          return dateStr;
                        }
                        const date = new Date(dateStr);
                        if (!isNaN(date.getTime())) {
                          return date.toISOString().split('T')[0];
                        }
                        return dateStr;
                      })() : ''}
                      onChange={(e) => setCurrentQuote({...currentQuote, valid_until: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">狀態 *</label>
                    <select
                      required
                      value={currentQuote.status}
                      onChange={(e) => setCurrentQuote({...currentQuote, status: e.target.value as Quote['status']})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    >
                      <option value="draft">草稿</option>
                      <option value="sent">已寄送</option>
                      <option value="signed_back">已簽回</option>
                      <option value="failed">失敗</option>
                      <option value="expired">已過期</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* 操作按鈕 */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCurrentQuote(null);
                    setIsEditing(false);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  取消
                </button>
                <button
                  onClick={saveQuote}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  儲存報價單
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 批量匯入模態框 */}
      {showBulkImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">批量匯入報價項目</h2>
              <button
                onClick={() => setShowBulkImport(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              {/* 匯入模式選擇 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  匯入模式
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="full"
                      checked={bulkImportMode === 'full'}
                      onChange={(e) => setBulkImportMode(e.target.value as 'full' | 'category')}
                      className="mr-2"
                    />
                    <span className="text-sm">完整模式（項目名稱,收費類別,數量）</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="category"
                      checked={bulkImportMode === 'category'}
                      onChange={(e) => setBulkImportMode(e.target.value as 'full' | 'category')}
                      className="mr-2"
                    />
                    <span className="text-sm">分類模式（項目名稱,數量）</span>
                  </label>
                </div>
              </div>

              {/* 分類模式下的收費類別選擇 */}
              {bulkImportMode === 'category' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    收費類別 * <span className="text-xs text-gray-500">(與新增項目邏輯一致)</span>
                  </label>
                  <select
                    value={selectedServiceType}
                    onChange={(e) => setSelectedServiceType(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">選擇收費類別</option>
                    {/* 如果有選擇合約且有合約服務項目，只顯示合約服務項目 */}
                    {currentQuote?.contract_id && contractServiceItems.length > 0 ? (
                      <>
                        {contractServiceItems.map(item => (
                          <option key={`contract-${item.id}`} value={item.service_type}>
                            {item.service_type} (合約價: {isMarkupRateUnit(item.unit) ? formatUnitPriceDisplay(item.contract_price, item.unit) : `${formatCurrency(item.contract_price)}/${item.unit}`})
                          </option>
                        ))}
                      </>
                    ) : (
                      /* 如果沒有合約或合約沒有服務項目，顯示服務收費表 */
                      <>
                        {getCurrentYearServiceRates().map(rate => {
                          const serviceRate = rate as ServiceRate;
                          const rateUnitPrice = serviceRate.unit_price || (serviceRate as unknown as { hourly_rate?: number }).hourly_rate || 0;
                          const unit = serviceRate.unit || '小時';
                          const priceDisplay = isMarkupRateUnit(unit) ? formatUnitPriceDisplay(rateUnitPrice, unit) : `${formatCurrency(rateUnitPrice)}/${unit}`;
                          return (
                            <option key={rate.id} value={rate.service_type}>
                              {selectedServiceType === rate.service_type ? '✓ ' : ''}{rate.service_type} ({priceDisplay})
                            </option>
                          );
                        })}
                      </>
                    )}
                  </select>
                </div>
              )}

              {/* 匯入資料 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  匯入資料
                </label>
                <textarea
                  value={bulkImportData}
                  onChange={(e) => setBulkImportData(e.target.value)}
                  rows={10}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
                  placeholder={
                    bulkImportMode === 'full' 
                      ? "項目名稱,收費類別,數量\n例如：\n系統分析,系統分析,8\n程式開發,程式開發,16\n測試驗收,測試驗收,4"
                      : "項目名稱,數量\n例如：\n系統分析,8\n程式開發,16\n測試驗收,4"
                  }
                />
              </div>

              {/* 說明 */}
              <div className="bg-primary/5 border border-primary/30 rounded-md p-4">
                <h4 className="text-sm font-medium text-primary-dark mb-2">匯入說明：</h4>
                <ul className="text-sm text-primary-dark/90 space-y-1">
                  <li>• 每行一個項目，欄位用逗號分隔</li>
                  <li>• 數量支援小數點</li>
                  <li>• 系統會自動根據合約載入對應價格（優先使用合約服務項目）</li>
                  <li>• 若無合約，使用當年度服務收費表價格</li>
                  <li>• 服務類型選擇與新增項目保持一致</li>
                </ul>
              </div>

              {/* 操作按鈕 */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowBulkImport(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  onClick={handleBulkImport}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                >
                  匯入項目
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 狀態修改模態框 */}
      {showStatusModal && statusQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">修改報價單狀態</h2>
              <button
                onClick={() => setShowStatusModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              {/* 報價單資訊 */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">報價單資訊</h4>
                <div className="text-sm text-gray-600">
                  <p><strong>報價單號：</strong>{statusQuote.quote_number}</p>
                  <p><strong>報價單名稱：</strong>{statusQuote.quote_name}</p>
                  <p><strong>客戶：</strong>{getCustomerName(statusQuote.customer_id)}</p>
                </div>
              </div>

              {/* 狀態選擇 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  新狀態 *
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="draft">草稿</option>
                  <option value="sent">已寄送</option>
                  <option value="signed_back">已簽回</option>
                  <option value="failed">失敗</option>
                  <option value="expired">已過期</option>
                </select>
              </div>

              {/* 簽回連結（僅在已簽回狀態時顯示） */}
              {newStatus === 'signed_back' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    簽回連結 *
                  </label>
                  <input
                    type="url"
                    value={signedBackFileUrl}
                    onChange={(e) => setSignedBackFileUrl(e.target.value)}
                    placeholder="https://example.com/signed-document"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    請填入已簽回報價單的連結地址
                  </p>
                </div>
              )}

              {/* 操作按鈕 */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  onClick={saveStatusChange}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                >
                  更新狀態
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 寄送郵件模態框 */}
      {showEmailModal && emailQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">寄送報價單郵件</h2>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* 左側：郵件設定 */}
              <div className="lg:col-span-1 space-y-4">
                {/* 報價單資訊 */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">報價單資訊</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>報價單號：</strong>{emailQuote.quote_number}</p>
                    <p><strong>報價單名稱：</strong>{emailQuote.quote_name}</p>
                    <p><strong>客戶：</strong>{getCustomerName(emailQuote.customer_id)}</p>
                    <p><strong>總金額：</strong>{formatCurrency(emailQuote.total_amount || 0)}</p>
                  </div>
                </div>

                {/* 郵件模板選擇 - 只顯示報價單類別 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    郵件模板 <span className="text-xs text-gray-500">(僅顯示報價單類別)</span>
                  </label>
                  <select
                    value={selectedTemplate?.id || ''}
                    onChange={(e) => {
                      const template = emailTemplates.find(t => t.id === e.target.value);
                      handleTemplateChange(template || null);
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">選擇郵件模板</option>
                    {emailTemplates.length > 0 ? (
                      emailTemplates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>暫無報價單郵件模板</option>
                    )}
                  </select>
                  {emailTemplates.length === 0 && (
                    <p className="mt-1 text-sm text-amber-600">
                      💡 提示：您可以在「設定」頁面建立報價單郵件模板
                    </p>
                  )}
                </div>

                {/* 收件人資訊 */}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      收件人郵件地址 *
                    </label>
                    <input
                      type="email"
                      value={emailData.recipientEmail}
                      onChange={(e) => setEmailData({...emailData, recipientEmail: e.target.value})}
                      placeholder="example@company.com"
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      收件人姓名
                    </label>
                    <input
                      type="text"
                      value={emailData.recipientName}
                      onChange={(e) => setEmailData({...emailData, recipientName: e.target.value})}
                      placeholder="收件人姓名"
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                </div>

                {/* 郵件主旨 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    郵件主旨
                  </label>
                  <input
                    type="text"
                    value={emailData.subject}
                    onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                    placeholder="郵件主旨"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                {/* 郵件內容 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    郵件內容
                  </label>
                  <textarea
                    value={emailData.body}
                    onChange={(e) => setEmailData({...emailData, body: e.target.value})}
                    rows={6}
                    placeholder="郵件內容"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                {/* 操作按鈕 */}
                <div className="flex flex-col space-y-2 pt-4">
                  <button
                    onClick={handleSendQuoteEmail}
                    disabled={isSendingEmail}
                    className="w-full px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSendingEmail ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        發送中...
                      </>
                    ) : (
                      '寄送郵件'
                    )}
                  </button>
                  <button
                    onClick={() => setShowEmailModal(false)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    取消
                  </button>
                </div>
              </div>

              {/* 右側：PDF 預覽 */}
              <div className="lg:col-span-2">
                <div className="bg-gray-50 rounded-lg p-4 h-full">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-sm font-medium text-gray-900">PDF 預覽</h4>
                    <button
                      onClick={handlePreviewPdf}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <EyeIcon className="w-4 h-4 mr-1" />
                      重新載入
                    </button>
                  </div>
                  {showPdfPreview && pdfPreviewUrl ? (
                    <div className="w-full h-[600px] border border-gray-300 rounded-lg overflow-hidden bg-white">
                      <iframe
                        src={pdfPreviewUrl}
                        className="w-full h-full"
                        title="PDF 預覽"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-[600px] border border-gray-300 rounded-lg flex items-center justify-center bg-white">
                      <div className="text-center text-gray-500">
                        <DocumentIcon className="w-16 h-16 mx-auto mb-4" />
                        <p className="text-lg font-medium mb-2">PDF 預覽</p>
                        <p className="text-sm">正在載入報價單 PDF...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Quotes; 